import { ColumnsMetadata } from "../interface/chat-data";

export function toCapitalCase(str: string): string {
	return str
		.toLowerCase()
		.replace(/\b\w/g, (char) => char.toUpperCase());
}

export function formatToFullDate(dateString: string): string {
	const date = new Date(dateString);
	return date.toLocaleDateString("en-GB", {
		weekday: "long",
		day: "2-digit",
		month: "short",
		year: "numeric",
	});
}

export function removeHidden(value: string): boolean {
	return value.toLowerCase().startsWith('hidden');
}

export function transformChartOption(data: any, colMetaData: ColumnsMetadata[], chartData: any): any {
	const { spec } = data;
	if (!spec || !spec.data || !chartData) {
		throw new Error("Invalid data format");
	}

	const values = chartData;
	const chartType = data.type;

	let baseOption: any = {
		chartType: chartType,
		tooltip: {
			trigger: chartType === 'pie_chart' || 'donut_chart' ? 'item' : 'axis',
			axisPointer: chartType === 'pie_chart' || 'donut_chart' ? undefined : { type: "shadow" },
		},
		series: [],
	};

	if (chartType === 'bar_chart' || chartType === 'line_chart' || chartType === 'column_chart' || chartType === 'area_chart' || chartType === 'step_line_chart' || chartType === 'scatter_plot') {
		const xField = spec.layer[0]?.encoding?.x?.field || "";
		const xType = spec.layer[0]?.encoding?.x?.type == 'quantitative' ? 'value' : 'category';
		const yField = spec.layer[0]?.encoding?.y?.field || "";
		const yType = spec.layer[0]?.encoding?.y?.type == 'quantitative' ? 'value' : 'category';

		baseOption = {
			...baseOption,
			series: [
				{
					type: chartType === 'bar_chart' || chartType === 'column_chart' ? 'bar' : 'line', // column becomes bar internally
					data: chartType === 'bar_chart' || chartType === 'column_chart'
						? values.map((item: any) => xType == 'value' ? item[xField] : item[yField] ?? 0)
						: chartType === 'scatter_plot' ?
							values.map((item: any) => [item[xField] ?? 0, item[yField] ?? 0])
							: values.map((item: any) => xType == 'value' ? item[xField] : item[yField] ?? 0),
					...(chartType === 'bar_chart' || chartType === 'column_chart' ? { barWidth: "50%" } : {}), // Conditional barWidth
					...(chartType === 'area_chart' ? { areaStyle: {} } : {}), // Conditional area
					...(chartType === 'step_line_chart' ? { step: 'start' } : {}) // Conditional step
				},
			],
		};

		if (chartType === 'bar_chart' || chartType === 'column_chart' || chartType === 'line_chart' || chartType === 'area_chart' || chartType === 'step_line_chart') {
			return {
				...baseOption,
				xAxis: [{
					type: xType,
					data: xType == 'category' ? values.map((item: any) => item[xField] ?? "Unknown") : [],
					name: getAxisTitle(xField, colMetaData),
				}],
				yAxis: [{
					type: yType,
					data: yType == 'category' ? values.map((item: any) => item[yField] ?? "Unknown") : [],
					name: getAxisTitle(yField, colMetaData),
				}],
			};
		} else { // scatter
			return {
				...baseOption,
				xAxis: [{
					type: "value",
					name: xField,
				}],
				yAxis: [{
					type: "value",
					name: yField,
				}],
			}
		}
	} else if (chartType === 'pie_chart' || chartType == 'donut_chart') {
		const thetaField = spec.layer[0]?.encoding?.theta?.field || "value";
		const colorField = spec.layer[0]?.encoding?.color?.field || "category";

		return {
			...baseOption,
			legend: {
				orient: "vertical",
				left: "left",
			},
			series: [
				{
					type: "pie",
					radius: chartType === 'pie_chart' ? "50%" : ["20%", "50%"],
					data: values.map((item: any) => ({
						name: item[colorField] ?? "Unknown",
						value: item[thetaField] ?? 0,
					})),
				},
			],
		};
	} else {
		// Handle invalid chart type (optional)
		return {}; // Or throw an error
	}
}

function getAxisTitle(axisColName: string, colMetaData: ColumnsMetadata[]) {
	let title: string = axisColName;
	let newTitle = colMetaData?.filter(x => x.colName == axisColName);
	return newTitle?.length > 0 ? newTitle[0].title : title;
}

export function extractYearAndValue(data: any[]) {
	const yearMap: Record<string, number> = {};

	data.forEach((item: { time_period_data: string | number | Date; value: string }) => {
		const date = new Date(item.time_period_data);
		const year = date.getFullYear().toString();
		const val = parseFloat(item.value) || 0;

		yearMap[year] = val;
	});

	// Round values to 2 decimals
	for (const year in yearMap) {
		yearMap[year] = parseFloat(yearMap[year].toFixed(10));
	}

	return yearMap;
}

export function extractYearAndValueForChart(data: Record<string, number>) {
	const years: string[] = [];
	const values: number[] = [];

	Object.entries(data).forEach(([year, value]) => {
		years.push(year);
		values.push(value);
	});

	return { years, values };
}


export function deepClonePreserveArrays<T>(input: T): T {
  // If it's an array, return the original array (preserve array reference)
  if (Array.isArray(input)) {
    return input;
  }
  // If it's a non-null object, clone each property recursively
  else if (input !== null && typeof input === 'object') {
    const clonedObj: any = {};
    for (const key in input) {
      if (Object.prototype.hasOwnProperty.call(input, key)) {
        clonedObj[key] = deepClonePreserveArrays((input as any)[key]);
      }
    }
    return clonedObj;
  }
  // For primitives, functions, null, or undefined, just return them
  return input;
}